import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';

import type { AlienAnimation } from '@/playlist/games/timeGame/types';

export type AlienHandle = {
	element: HTMLDivElement | null;
	playAnswerAnimation: (isCorrect: boolean, onComplete?: () => void) => void;
};

interface AlienProps {
	alien: AlienAnimation;
	index: number;
	leftOffset: number;
}

const Alien = forwardRef<AlienHandle, AlienProps>(function Alien(
	{ alien, index, leftOffset },
	ref,
) {
	const alienRef = useRef<HTMLDivElement | null>(null);
	const [isRemoved, setIsRemoved] = useState(false);
	const [currentAnimation, setCurrentAnimation] = useState(alien.idleAnimation);
	const [shouldLoop, setShouldLoop] = useState(true);
	const [isAnswerCorrect, setIsAnswerCorrect] = useState<boolean | null>(null);
	const lottieRef = useRef<LottieRefCurrentProps>(null);
	const onCompleteCallbackRef = useRef<(() => void) | null>(null);

	const triggerAnswerAnimation = useCallback(
		(isCorrect?: boolean, onComplete?: () => void) => {
			if (
				currentAnimation !== alien.idleAnimation &&
				currentAnimation !== alien.irritatedAnimation
			)
				return;
			const correct = isCorrect !== false;
			setCurrentAnimation(correct ? alien.correctAnimation : alien.wrongAnimation);
			setShouldLoop(false);
			setIsAnswerCorrect(correct);
			onCompleteCallbackRef.current = onComplete || null;
		},
		[currentAnimation, alien.idleAnimation, alien.correctAnimation, alien.wrongAnimation],
	);

	const handleAnimationComplete = () => {
		const playedAnswerAnimation =
			currentAnimation === alien.correctAnimation ||
			currentAnimation === alien.wrongAnimation;

		if (playedAnswerAnimation) {
			setCurrentAnimation(alien.irritatedAnimation);
			setShouldLoop(true);
			if (isAnswerCorrect) {
				setIsRemoved(true);
			}
		}

		if (!isAnswerCorrect) {
			if (alienRef.current) {
				alienRef.current.style.transform = '';
				alienRef.current.style.marginBottom = '';
			}
		}

		if (onCompleteCallbackRef.current) {
			onCompleteCallbackRef.current();
			onCompleteCallbackRef.current = null;
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			element: alienRef.current,
			playAnswerAnimation: (isCorrect: boolean, onComplete?: () => void) =>
				triggerAnswerAnimation(isCorrect, onComplete),
		}),
		[triggerAnswerAnimation],
	);

	if (isRemoved) {
		return null;
	}

	return (
		<div
			className={classNames('conveyor-belt__alien')}
			style={{ left: `${leftOffset}%` }}
			data-alien-index={index}
			data-alien-id={alien.id}
			ref={alienRef}
		>
			<div className="conveyor-belt__alien-art">
				<Lottie
					animationData={currentAnimation}
					loop={shouldLoop}
					autoplay
					lottieRef={lottieRef}
					className="conveyor-belt__alien-animation"
					onComplete={handleAnimationComplete}
				/>
			</div>
		</div>
	);
});

export default Alien;
